import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { localeToApiLanguage } from '~/utils/languageFormat';
import { useCurrentUser } from './useCurrentUser';
import { useMemberViewerRelation } from './useMemberViewerRelation';

export type HelpPlacement = components['schemas']['HelpPlacement'];

/**
 * The language both help reads pass as a query parameter rather than relying on
 * the `x-wbs-language` header.
 *
 * Help content is partitioned by language with no fallback, and the header
 * isn't part of the query key; subscribing to i18n here re-keys the query, so
 * an open modal doesn't keep the previous language's articles after a switch.
 */
export function useHelpLanguage(): components['schemas']['Language'] {
  const { i18n: instance } = useTranslation();
  // `resolvedLanguage`, not `language`, which may be a code we don't ship. See
  // `apiLanguage()`.
  return localeToApiLanguage(instance.resolvedLanguage ?? instance.language);
}

/**
 * Which help anchors resolve to a section in the interface language. One
 * request per language per session, shared by every `TitleWithQuestion` through
 * the cache.
 *
 * Tri-state: `undefined` means not answered yet, which `TitleWithQuestion`
 * treats as allowed, so a heading's `?` paints on first render rather than a
 * round trip late. Nearly every placement resolves, so this rarely excludes
 * anything; it exists because legacy's icon is gated on the same check.
 *
 * The language switch already invalidates every query (`useInterfaceLanguage`),
 * so nothing extra refetches this.
 */
export function useHelpPlacements(): Set<HelpPlacement> | undefined {
  const { me } = useCurrentUser();
  const language = useHelpLanguage();

  // The placements endpoint needs a signed-in user and `/_app` isn't
  // auth-gated, so an ungated query would 401 on every signed-out page load.
  // Signed out, this returns `undefined` too, which is why the global `?` is
  // gated on `me` where it mounts.
  const { data } = api.listContextualHelpPlacements.useQuery(
    { query: { language } },
    { enabled: !!me },
  );

  return useMemo(() => (data ? new Set(data.placements) : undefined), [data]);
}

/**
 * The help placement for a member page, which legacy picks from the viewer's
 * relation to the member rather than from the screen.
 *
 * Arms 2 and 3 test the displayed member's type, which on your own profile is
 * you. Only student and study helper types match, so admins and follow-up
 * coordinators and partners get no icon, which is why
 * `useCurrentUser().isStudyHelper` can't be used here.
 */
export function useProfileHelpPlacement(
  memberId: string | undefined,
): HelpPlacement | undefined {
  const { me } = useCurrentUser();
  // A non-numeric sentinel where the route carries no member, so
  // `useMemberViewerRelation` disables both of its probes: it gates them on
  // `Number.isFinite`, and `Number('')` is 0, which passes.
  const { isSelf, isAssignedStudyHelper } = useMemberViewerRelation(
    memberId ?? 'none',
  );

  if (memberId === undefined) return undefined;
  if (isAssignedStudyHelper) return 'student_profile_for_study_helpers';
  if (!isSelf) return undefined;
  if (me?.profile_type === 'STUDY_HELPER') return 'study_helper_profile';
  return me?.profile_type === 'WEB_STUDENT' ||
    me?.profile_type === 'POSTAL_STUDENT' ||
    me?.profile_type === 'LITEAPP_STUDENT' ||
    me?.profile_type === 'EMAIL_STUDENT'
    ? 'student_profile'
    : undefined;
}
